<template>
  <k-responsive :aspect-ratio="16/9" class="ks-video-player">
    <slot v-if="!loadPlayer"></slot>
    <div class="start-container elevation-0" v-if="!started && loadPlayer && !unavailable" @click="play">
      <k-btn icon v-if="!checking"><k-icon x-large>play_arrow</k-icon></k-btn>
    </div>
    <div class="start-container unavailable-container elevation-0" v-if="!started && loadPlayer && unavailable">
      <div class="unavailable-text">Video is not available</div>
      <k-btn icon @click="retry"><k-icon x-large>refresh</k-icon></k-btn>
    </div>

    <div v-if="started" style="width: 100%; height: 100%; display: flex;" ref="player_container" class="player-container">
      <video style="width: 100%; height: 100%; padding:0px; object-fit: contain;" id="video" preload="metadata" playsinline ref="player" class="elevation-0" @click="play_toggle"></video>
      <div v-if="buffering" class="buffering-indicator">
        <k-progress-circular indeterminate :size="24" :width="3" color="white"></k-progress-circular>
      </div>
      <!-- Seek bar -->
      <div 
        v-if="!is_live && duration > 0"
        class="seek-bar-container"
        :class="{'hide-bar': !show_bar, 'seek-bar-tall': tallBar}"
        @mouseenter="seek_hover = true"
        @mouseleave="seek_hover = false"
        @mousemove="onSeekHover"
        @click="onSeekClick"
      >
        <!-- Hover timestamp tooltip -->
        <div 
          v-if="seek_hover" 
          class="seek-tooltip"
          :style="{ left: seek_tooltip_left + 'px' }"
        >
          {{ seek_tooltip_time }}
        </div>
        <!-- Progress (played portion) -->
        <div class="seek-bar-progress" :style="{ width: seek_progress + '%' }"></div>
        <!-- Playback position thumb -->
        <div class="seek-bar-thumb" :class="{'seek-bar-thumb-dragging': is_dragging}" :style="{ left: seek_progress + '%' }" @mousedown.prevent="onThumbDragStart"></div>
        <!-- Hover indicator -->
        <div v-if="seek_hover" class="seek-bar-hover" :style="{ width: seek_hover_percent + '%' }"></div>
        <!-- Hover position marker -->
        <div v-if="seek_hover" class="seek-bar-hover-marker" :style="{ left: seek_hover_percent + '%' }"></div>
      </div>

      <div class="control-bar" :class="{'hide-bar': !show_bar, 'control-bar-seek': !is_live, 'control-bar-tall': tallBar}">
        <k-btn icon style="height: 24px;" @click="play_toggle"><k-icon size="l">{{play_icon}}</k-icon></k-btn>
        <!-- Current time / Duration display -->
        <div class="seek-time-display" :style="{ visibility: duration > 0 ? 'visible' : 'hidden' }">
          {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
        </div>
        <div class="seek-time-display-placeholder">
          <template v-if="back_to_live">
            <k-btn style="height: 24px !important; margin-left: 4px; margin-top: 2px" size="xs" flat no-margin @click="seek_back_to_live">
              <k-icon small color="red">circle</k-icon><span class="live-text">&nbsp;Live</span>
            </k-btn>
          </template>
        </div>

        <k-btn icon style="height: 24px;" @click="refresh"><k-icon size="l">refresh</k-icon></k-btn>
        <k-btn icon style="height: 24px;" @click="mute_toggle"><k-icon size="l">{{volume_icon}}</k-icon></k-btn>

        <div :style="{width: (fullscreen ? '120px' : '60px')}" style="margin-right: 8px; margin-left: 8px; height: 22px;">
          <k-slider 
            v-model="current_volume" 
            hide-details 
            style="margin-top: -2px;" 
            color="var(--ks-col-primary)" 
            :min="0.0" 
            :max="1.0" 
            :step="0.0001">
          </k-slider>
        </div>
        <k-btn icon style="height: 24px;" @click="fullscreen_toggle"><k-icon size="l">{{fullscreen_icon}}</k-icon></k-btn>
      </div>
    </div>
  </k-responsive>
</template>
<script>
  import screenfull from '@/lib/screenfull.js'
  import Hls from 'hls.js'
  export default {
    props: {
      "load-player": {
        type: Boolean,
        default: true
      }, 
      "url": String, 
      "autoplay": {
        type: Boolean,
        default: false
      },
      "position": Number,
      "hide-bar": {
        type: Boolean,
        default: true
      },
      volume: {
        type: Number,
        default: 0.0001
      },
      "pause-on-refresh": {
        type: Boolean,
        default: false
      },
      "tall-bar": {
        type: Boolean,
        default: true
      },
      "frame-accurate-seek": {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        video: undefined,
        hls: undefined,
        started: false,
        playing: false,
        muted: false,
        current_volume: this.volume,
        fullscreen: false,
        show_bar: true,
        currentTime: 0,
        duration: 0,
        seek_hover: false,
        seek_hover_percent: 0,
        seek_tooltip_left: 0,
        seek_tooltip_time: '0:00',
        clip_end: undefined,
        resume_time: undefined,
        resume_playing: undefined,
        pending_time: undefined,
        buffering: false,
        first_frame_loaded: false,
        is_dragging: false,
        was_playing_before_drag: false,
        _onDragMove: null,
        _onDragEnd: null,
        is_live: false,
        back_to_live: false,
        checking: false,
        unavailable: false
      }
    },
    watch: {
      volume(newVal) {
        this.current_volume = newVal;
      },
      current_volume(newVal) {
        if(this.video) this.video.volume = newVal;
      },
      loadPlayer(newVal) {
        if (newVal && !this.started) this.check_availability();
      },
      url() {
        this.unavailable = false;
        if (!this.started) {
          this.check_availability();
          return;
        }
        this.switch_source();
      },
      "position": function(new_position) {
        if (new_position > -1) {
          if (typeof(this.pending_time) != "undefined") {
            this.pending_time = new_position;
            return;
          }
          this.video.currentTime = new_position;
        }
      }
    },
    computed: {
      play_icon() {
        if(this.playing) return "pause";
        return "play_arrow";
      },
      volume_icon() {
        if(this.muted || this.current_volume == 0) return "volume_off";
        if(this.current_volume < 0.5) return "volume_down";
        return "volume_up";
      },
      fullscreen_icon() {
        if(this.fullscreen) return "fullscreen_exit";
        return "fullscreen";
      },
      seek_progress() {
        if (this.duration <= 0) return 0;
        return (this.currentTime / this.duration) * 100;
      }
    },
    methods: {
      play_clip(start, stop) {
        this.video.currentTime = start;
        this.video.play();
        this.clip_end = stop;
      },


      step(change) {
        if (!this.video) return;
        // Seeking before data is buffered makes hls.js adopt that time as its start position
        if (typeof(this.pending_time) != "undefined") {
          this.pending_time = Math.max(0, this.pending_time + change);
          return;
        }
        if (!this.started) return;
        var time = this.video.currentTime + change;
        if (this.duration > 0) time = Math.min(time, this.duration);
        this.video.currentTime = Math.max(0, time);
      },
      start_frame_tracking() {
        var video = this.video;
        this._frame_samples = [];
        this._frame_media_time = undefined;
        this._frame_presented = undefined;
        if (!this.frameAccurateSeek) return;
        if (!video || !video.requestVideoFrameCallback) return;
        var on_frame = (now, meta) => {
          if (video !== this.video) return;
          if (typeof(this._frame_media_time) != "undefined") {
            var frames = meta.presentedFrames - this._frame_presented;
            var delta = meta.mediaTime - this._frame_media_time;
            // Ignore seeks and long gaps; dropped frames are divided out
            if (frames > 0 && delta > 0 && delta / frames < 0.1) {
              this._frame_samples.push(delta / frames);
              if (this._frame_samples.length > 30) this._frame_samples.shift();
            }
          }
          this._frame_media_time = meta.mediaTime;
          this._frame_presented = meta.presentedFrames;
          video.requestVideoFrameCallback(on_frame);
        };
        video.requestVideoFrameCallback(on_frame);
      },
      frame_duration() {
        if (!this.frameAccurateSeek) return this._manifest_fps > 0 ? 1 / this._manifest_fps : 1 / 50;
        var samples = this._frame_samples || [];
        if (samples.length >= 5) {
          var sorted = samples.slice().sort((a, b) => a - b);
          return sorted[Math.floor(sorted.length / 2)];
        }
        if (this._manifest_fps > 0) return 1 / this._manifest_fps;
        if (samples.length > 0) return Math.min.apply(null, samples);
        return 1 / 50;
      },
      step_frame(count) {
        if (!this.video || !this.started || typeof(this.pending_time) != "undefined") return;
        if (this.playing) this.video.pause();
        var fd = this.frame_duration();
        var base = typeof(this._frame_media_time) != "undefined" ? this._frame_media_time : this.video.currentTime;
        // Aim at the middle of the target frame so rounding cannot land on a neighbour
        var time = base + count * fd + fd / 2;
        if (this.duration > 0) time = Math.min(time, this.duration);
        this.video.currentTime = Math.max(0, time);
      },
      fullscreenchanged() {
        if (screenfull.isFullscreen) {
          this.fullscreen = true;
        } else {
          this.fullscreen = false;
        }
      },
      fullscreen_toggle() {
        if(!this.video) return;
        var player = this.$refs.player_container
        if (screenfull.isEnabled) {
          screenfull.toggle(player);
        }
      },
      play_toggle() {
        if(!this.video) return;
        if(this.playing) {
          this.video.pause();
        }
        if(!this.playing) {
          this.video.play();
        }
      },
      refresh() {
        if (this._refreshing) return;
        this._refreshing = true;
        
        // currentTime lags behind pending seeks, so read the element itself
        var time = this.started && this.video ? this.video.currentTime : this.currentTime;
        if (typeof(this.pending_time) != "undefined") time = this.pending_time;
        if (!this.is_live && time > 0) {
          this.resume_time = time;
          this.resume_playing = this.playing;
        }
        if (!this.is_live) this.pending_time = time;
        if (this.pauseOnRefresh) this.resume_playing = false;
        
        this.playing = false;
        if (this.hls) {
          this.hls.destroy();
          this.hls = undefined;
        }
        
        // Stop and reset video without removing it from DOM
        if (this.video) {
          this.video.pause();
          this.video.src = '';
          this.video.load();
        }
        
        this.$nextTick(async () => {
          try {
            await this.check_availability();
            if (!this.unavailable && this.started) {
              this.play();
            }
          } finally {
            this._refreshing = false;
          }
        });
      },
      switch_source() {
        this.playing = false;
        this.resume_time = undefined;
        this.resume_playing = undefined;
        this.pending_time = undefined;
        this.clip_end = undefined;
        this.is_live = false;
        this.back_to_live = false;
        this.currentTime = 0;
        this.duration = 0;
        if (this.hls) {
          this.hls.destroy();
          this.hls = undefined;
        }
        if (this.video) {
          this.video.pause();
          this.video.src = '';
          this.video.load();
        }
        if (!this.url || !this.loadPlayer) return;
        // A user-selected source should start playing instead of holding the first frame
        this._force_play = true;
        this.play();
      },
      formatTime(seconds) {
        if (!isFinite(seconds) || seconds < 0) return '00:00';
        var h = Math.floor(seconds / 3600);
        var m = Math.floor((seconds % 3600) / 60);
        var s = Math.floor(seconds % 60);
        var sStr = s < 10 ? '0' + s : '' + s;
        if (h > 0) {
          var mStr = m < 10 ? '0' + m : '' + m;
          return h.toString().padStart(2, '0') + ':' + mStr + ':' + sStr;
        }
        return m + ':' + sStr;
      },
      mediaErrorCodeToName(code) {
        if (code === 1) return 'MEDIA_ERR_ABORTED';
        if (code === 2) return 'MEDIA_ERR_NETWORK';
        if (code === 3) return 'MEDIA_ERR_DECODE';
        if (code === 4) return 'MEDIA_ERR_SRC_NOT_SUPPORTED';
        return 'UNKNOWN_MEDIA_ERROR';
      },
      logVideoError(event, extra) {
        var mediaError = this.video && this.video.error ? this.video.error : null;
        var details = {
          eventType: event && event.type ? event.type : 'unknown',
          src: this.video ? (this.video.currentSrc || this.video.src || null) : null,
          networkState: this.video ? this.video.networkState : null,
          readyState: this.video ? this.video.readyState : null,
          browserMessage: event && event.message ? event.message : null,
          mediaError: mediaError ? {
            code: mediaError.code,
            codeName: this.mediaErrorCodeToName(mediaError.code),
            message: typeof mediaError.message === 'string' ? mediaError.message : null
          } : null
        };

        if (extra) {
          details.extra = extra;
        }

        console.error('[KVideoPlayer] Video playback error', details);
      },
      onSeekHover(event) {
        var rect = event.currentTarget.getBoundingClientRect();
        var x = event.clientX - rect.left;
        var percent = (x / rect.width) * 100;
        percent = Math.max(0, Math.min(100, percent));
        this.seek_hover_percent = percent;
        var time = (percent / 100) * this.duration;
        this.seek_tooltip_time = this.formatTime(time);
        // Keep tooltip within bounds
        var tooltipWidth = 52;
        var left = x - (tooltipWidth / 2);
        left = Math.max(0, Math.min(rect.width - tooltipWidth, left));
        this.seek_tooltip_left = left;
      },
      onSeekClick(event) {
        if (this.is_dragging) return;
        if (!this.video || this.duration <= 0) return;
        var rect = event.currentTarget.getBoundingClientRect();
        var x = event.clientX - rect.left;
        var percent = x / rect.width;
        percent = Math.max(0, Math.min(1, percent));
        this.video.currentTime = percent * this.duration;
        this.currentTime = percent * this.duration;
      },
      onThumbDragStart(event) {
        if (!this.video || this.duration <= 0) return;
        this.is_dragging = true;
        this.was_playing_before_drag = this.playing;
        this.video.pause();
        var self = this;
        this._onDragMove = function(e) {
          var container = self.$el.querySelector('.seek-bar-container');
          if (!container) return;
          var rect = container.getBoundingClientRect();
          var x = e.clientX - rect.left;
          var percent = Math.max(0, Math.min(1, x / rect.width));
          self.video.currentTime = percent * self.duration;
          self.currentTime = percent * self.duration;
          // Update tooltip
          self.seek_hover = true;
          self.seek_hover_percent = percent * 100;
          self.seek_tooltip_time = self.formatTime(percent * self.duration);
          var tooltipWidth = 52;
          var left = x - (tooltipWidth / 2);
          left = Math.max(0, Math.min(rect.width - tooltipWidth, left));
          self.seek_tooltip_left = left;
        };
        this._onDragEnd = function() {
          self.is_dragging = false;
          self.seek_hover = false;
          if (self.was_playing_before_drag) {
            self.video.play();
          }
          document.removeEventListener('mousemove', self._onDragMove);
          document.removeEventListener('mouseup', self._onDragEnd);
        };
        document.addEventListener('mousemove', this._onDragMove);
        document.addEventListener('mouseup', this._onDragEnd);
      },
      mute_toggle() {
        if(!this.video) return;
        this.video.muted = !this.video.muted;
        this.muted = this.video.muted;
      },
      seek_back_to_live() {
        if (!this.is_live) return;
        if (!this.hls) return;
        this.video.currentTime = this.hls.liveSyncPosition;
        this.back_to_live = false;
      },
      async check_url() {
        if (!this.url) return false;
        var controller = new AbortController();
        try {
          var response = await fetch(this.url, { method: 'GET', signal: controller.signal });
          return response.ok;
        } catch (e) {
          return false;
        } finally {
          controller.abort();
        }
      },
      async check_availability() {
        if (!this.url || !this.loadPlayer) return;
        var token = this._check_token = (this._check_token || 0) + 1;
        this.checking = true;
        this.unavailable = false;
        var available = await this.check_url();
        if (token !== this._check_token) return;
        this.checking = false;
        this.unavailable = !available;
      },
      async retry() {
        await this.check_availability();
        if (!this.unavailable) this.play();
      },
      set_buffering(on) {
        if (!on) {
          clearTimeout(this._buffering_timer);
          this._buffering_timer = undefined;
          this.buffering = false;
          return;
        }
        if (this.buffering || this._buffering_timer) return;
        this._buffering_timer = setTimeout(() => {
          this._buffering_timer = undefined;
          this.buffering = true;
        }, 500);
      },
      play() {
        var self = this;
        this.started = true;
        this.$nextTick(() => {
          var autoplay = this.autoplay;
          this.video = this.$refs.player;
          this.set_buffering(true);
          var video_el = this.video;
          var buffering_handler = (value) => () => { if (this.video === video_el) this.set_buffering(value); };
          ['loadstart', 'waiting', 'seeking'].forEach(name => video_el.addEventListener(name, buffering_handler(true)));
          ['loadeddata', 'canplay', 'playing', 'seeked', 'error'].forEach(name => video_el.addEventListener(name, buffering_handler(false)));
          this._manifest_fps = undefined;
          this.start_frame_tracking();
          if (Hls.isSupported()) {
            // Safari's ManagedMediaSource can evict buffers and break seeking
            var hls_config = { debug: false, preferManagedMediaSource: false };
            if (typeof(this.resume_time) != "undefined") {
              hls_config.startPosition = this.resume_time;
            }
            this.hls = new Hls(hls_config);
            this.hls.loadSource(this.url);
            this.hls.attachMedia(this.video);
            this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
              this.video.volume = this.current_volume;
            });
            var start_decided = false;
            this.hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
              const details = data.details;
              if (details) {
                self.is_live = details.live;
              }
              if (!start_decided) {
                start_decided = true;
                // On the very first VOD load stay paused so only the first frame is shown
                var hold_first_frame = !self.is_live && !self.first_frame_loaded && !self._force_play;
                if (!hold_first_frame && self.resume_playing !== false) self.video.play();
                self._force_play = false;
                self.resume_time = undefined;
                self.resume_playing = undefined;
              }
              var level = self.hls && self.hls.levels ? self.hls.levels[data.level] : undefined;
              var fps = level && level.attrs ? parseFloat(level.attrs['FRAME-RATE']) : NaN;
              if (fps > 0) self._manifest_fps = fps;
            });
            this.hls.on(Hls.Events.ERROR, (event, data) => {
              this.logVideoError(event, {
                source: 'hls.js',
                type: data ? data.type : null,
                details: data ? data.details : null,
                fatal: data ? data.fatal : null,
                reason: data && data.reason ? data.reason : null,
                response: data && data.response ? data.response : null
              });
            });
            this.video.addEventListener("volumechange", (event) => {
              this.current_volume = this.video.volume;
            });
            this.video.addEventListener("play", (event) => {
              this.playing = true;
              if (this.hideBar) {
                this.show_bar = false;
              }

            })
            this.video.addEventListener("pause", (event) => {
              this.playing = false;
              if (this.hideBar) {
                this.show_bar = true;
              }
            })
            this.video.addEventListener("error", (event) => {
              this.logVideoError(event);
            });
            this.video.addEventListener("playing", (event) => {
              this.$emit("input", {
                width: this.video.videoWidth,
                height: this.video.videoHeight
              })
            })
            var video = this.video;
            this.video.addEventListener("timeupdate", () => {
              if (video !== this.video) return;
              this.currentTime = this.video.currentTime;
              this.duration = this.video.duration || 0;

              if (this.is_live) {
                if (this.currentTime < this.hls.liveSyncPosition - 3.0) {
                  this.back_to_live = true;
                }
              }
              this.$emit("position-duration", {
                position: this.currentTime,
                duration: this.duration
              });
              if (typeof(this.clip_end) != "undefined") {
                if (this.currentTime > this.clip_end) {
                  this.video.pause();
                  this.clip_end = undefined;
                  this.$emit("playing-clip", false);
                }
              }
            })
            this.video.addEventListener("loadedmetadata", () => {
              this.duration = this.video.duration || 0;
            })
            this.video.addEventListener("loadeddata", () => {
              if (video !== this.video) return;
              var target = this.pending_time;
              this.pending_time = undefined;
              if (typeof(target) != "undefined" && Math.abs(this.video.currentTime - target) > 0.5) {
                this.video.currentTime = target;
              }
              if (!this.is_live && this.first_frame_loaded == false) {
                this.first_frame_loaded = true;
                this.$emit("loaded");
              }
            })
          }
          else if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
            this.video.src = this.url;
            var resume_playing = this.resume_playing;
            this.resume_time = undefined;
            this.resume_playing = undefined;
            this.video.addEventListener('canplay', () => {
              if (resume_playing !== false) this.video.play();
            });
          }
        })

      }
    },
    mounted() {
      this.check_availability();
      if (screenfull.isEnabled) {
        screenfull.on('change', this.fullscreenchanged);
      }
    },
    beforeDestroy() {
      clearTimeout(this._buffering_timer);
      if(this.hls) this.hls.destroy();
      if (this._onDragMove) document.removeEventListener('mousemove', this._onDragMove);
      if (this._onDragEnd) document.removeEventListener('mouseup', this._onDragEnd);
    }
  }
</script>
<style scoped>
/* Own stacking context: the internal layers below only order player parts */
.ks-video-player {
  isolation: isolate;
}
.start-container {
  width: 100%; 
  height: 100%; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  background: var(--ks-background-color-dark);
}


.unavailable-container {
  flex-direction: column;
  color: rgba(255, 255, 255, 0.85);
}
.unavailable-text {
  font-size: 14px;
}

.buffering-indicator {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  display: flex;
  padding: 4px;
  border-radius: 50%;
  background: var(--ks-background-color-op60);
  pointer-events: none;
}

.hide-bar {
  transition: opacity 0.2s ease-out;
  opacity: 0;
}
.player-container {
  width: 100%; 
  height: 100%; 
  display: flex;
  background: var(--ks-background-color-dark);
}

.player-container:hover .hide-bar {
  opacity: 1;
}
.control-bar {
  background-color: var(--ks-background-color-op60); 
  position: absolute; 
  height: 28px; 
  bottom: 0px; 
  left: 0px; 
  width: 100%; 
  z-index: 1;
  display: grid; 
  grid-template-columns: 28px minmax(0, 1fr) 28px 28px max-content 34px;
}
.control-bar-tall {
  box-sizing: border-box;
  height: 32px;
  padding-top: 4px;
}
.seek-bar-container.seek-bar-tall {
  bottom: 22px;
}
.control-bar .seek-time-display {
  display: none;
}
.control-bar .seek-time-display-placeholder {
  min-width: 0;
  overflow: hidden;
  container-type: inline-size;
}
@container (max-width: 70px) {
  .live-text {
    display: none;
  }
}


.control-bar-seek {
  grid-template-columns: 28px minmax(0, 1fr) 28px 28px max-content 34px;
}
.control-bar-seek .seek-time-display {
  display: flex;
  min-width: 0;
  overflow: hidden;
  font-family: Roboto;
}
.control-bar-seek .seek-time-display-placeholder {
  display: none;
}


.seek-bar-container {
  position: absolute;
  bottom: 18px;
  left: 0;
  width: 100%;
  height: 23px;
  background: transparent;
  z-index: 2;
  cursor: pointer;
}
.seek-bar-container::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 3px;
  transform: translateY(-50%);
  background: var(--ks-col-info);
  pointer-events: none;
  z-index: 0;
}
.seek-bar-progress {
  position: absolute;
  top: 50%;
  left: 0;
  height: 3px;
  transform: translateY(-50%);
  background: var(--ks-col-warning-alternative);
  pointer-events: none;
  z-index: 2;
}
.seek-bar-thumb {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  background: var(--ks-col-warning-alternative);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: auto;
  z-index: 3;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.4);
  cursor: pointer;
}
.seek-bar-thumb:active,
.seek-bar-thumb-dragging {
  cursor: grabbing;
  transform: translate(-50%, -50%) scale(1.4);
}
.seek-bar-container:hover .seek-bar-thumb {
  transform: translate(-50%, -50%) scale(1.3);
}
.seek-bar-hover {
  position: absolute;
  top: 50%;
  left: 0;
  height: 3px;
  transform: translateY(-50%);
  pointer-events: none;
  background: var(--ks-col-warning);
  z-index: 1;
}
.seek-bar-hover-marker {
  position: absolute;
  top: 50%;
  width: 4px;
  height: 8px;
  transform: translate(-50%, -50%);
  background: #fff;
  border-radius: 2px;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
  pointer-events: none;
  z-index: 4;
}
.seek-tooltip {
  position: absolute;
  bottom: 100%;
  margin-bottom: 6px;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 12px;
  padding: 3px 6px;
  border-radius: 3px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
  font-variant-numeric: tabular-nums;
}
.seek-time-display {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  padding: 0 8px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
</style>
<style>
  /* Safari syntax */
  :-webkit-full-screen {
    background-color: black;
  }

  /* IE11 */
  :-ms-fullscreen {
    background-color: black;
  }

  /* Standard syntax */
  :fullscreen {
    background-color: black;
  }
</style>